<template>
  <div>
    <h1 class="mb-2 mt-1">Products</h1>

    <b-card no-body>
      <loading-container v-if="products.loading" />
      <error-display v-else-if="products.error" />
      <no-data-container
        v-else-if="filteredProducts.length === 0"
        message="No products found"
      />
      <div v-else>
        <b-table
          responsive
          hover
          :items="filteredProducts"
          :fields="products.fields"
          :sort-by.sync="products.sortBy"
          :sort-desc.sync="products.sortDesc"
          :no-local-sorting="products.totalRows !== undefined"
          @row-clicked="handleRowClick"
        >
          <template #cell(producttype)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                v-if="data.item.pticonname"
                class="mr-1"
                :variant="data.item.pticoncolor"
              >
                <feather-icon
                  size="18"
                  :icon="data.item.pticonname"
                />
              </b-avatar>
              <span>{{ data.item.producttypename }}</span>
            </div>
          </template>

          <template #cell(productname)="data">
            <div class="d-flex align-items-center">
              <b-img
                v-if="data.item.providerid"
                :src="`${baseURL}serveproviderlogo.cfm?id=${data.item.providerid}`"
                alt="provider logo"
                class="mr-1"
                style="width: 32px; height: 32px; object-fit: contain;"
              />
              <span>{{ data.item.productname }}</span>
            </div>
          </template>

          <template #cell(date_1_strike)="data">
            <div class="d-flex flex-column gap-0.5">
              <span class="text-center">{{ formatIsoDate(data.item.date_1_strike) }}</span>
            </div>
          </template>

          <template #cell(date_2_finalmarket)="data">
            <div class="d-flex flex-column gap-0.5">
              <span class="text-center">{{ formatIsoDate(data.item.date_2_finalmarket) }}</span>
            </div>
          </template>
        </b-table>

        <div class="d-flex p-2 align-items-center" :class="showPagination ? 'justify-content-between' : 'justify-content-start'">
          <span>Showing results {{ showingResults }}</span>
          <b-pagination
            v-if="showPagination"
            v-model="products.currentPage"
            :total-rows="products.totalRows !== undefined ? products.totalRows : filteredProducts.length"
            :per-page="products.perPage"
            align="center"
            size="sm"
            class="my-0"
          />
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BPagination,
  BTable,
  BAvatar,
  BImg,
} from 'bootstrap-vue'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import ErrorDisplay from '@/views/components/ErrorDisplay.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'
import { formatIsoDate } from '@/utils/formatters'

export default {
  components: {
    BCard,
    BPagination,
    BTable,
    BAvatar,
    BImg,
    LoadingContainer,
    ErrorDisplay,
    NoDataContainer,
  },
  props: {
    products: {
      type: Object,
      required: true,
      // Expected format:
      // {
      //   data: [],
      //   loading: false,
      //   error: false,
      //   search: '',
      //   fields: [...],
      //   sortBy: null,
      //   sortDesc: false,
      //   perPage: 50,  // optional
      //   currentPage: 1  // optional
      // }
    },
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
    }
  },
  computed: {
    showPagination() {
      return this.products.perPage !== undefined && this.products.currentPage !== undefined
    },
    filteredProducts() {
      // Return products as-is, since filtering is now handled server-side
      return this.products.data || []
    },
    showingResults() {
      if (this.products.totalRows !== undefined) {
        // Server-side pagination
        const from = (this.products.currentPage - 1) * this.products.perPage + 1
        const to = Math.min(this.products.currentPage * this.products.perPage, this.products.totalRows)
        return `from ${from} to ${to}`
      }
      // Client-side pagination fallback
      if (this.showPagination) {
        const from = (this.products.currentPage - 1) * this.products.perPage + 1
        let to = this.products.currentPage * this.products.perPage
        to = to > this.filteredProducts.length ? this.filteredProducts.length : to
        return `from ${from} to ${to}`
      }
      const from = 1
      const to = this.filteredProducts.length
      return `from ${from} to ${to}`
    },
  },
  methods: {
    formatIsoDate,
    sortCompare(aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale) {
      // Sort 'n.a' and 'none' values of closingDate to the top else sort normally
      if (key === 'closingDate') {
        const a = aRow[key]
        const b = bRow[key]
        if (a === 'n.a.' || a === 'none') {
          return -1
        } if (b === 'n.a.' || b === 'none') {
          return 1
        }
      }

      const a = aRow[key]
      const b = bRow[key]
      if (
        (typeof a === 'number' && typeof b === 'number')
        || (a instanceof Date && b instanceof Date)
      ) {
        // eslint-disable-next-line no-nested-ternary
        return a < b ? -1 : a > b ? 1 : 0
      }
      return this.toString(a).localeCompare(this.toString(b), compareLocale, compareOptions)
    },
    toString(value) {
      if (value === null || typeof value === 'undefined') {
        return ''
      } if (value instanceof Object) {
        return Object.keys(value)
          .sort()
          .map(key => this.toString(value[key]))
          .join(' ')
      }
      return String(value)
    },
    handleRowClick(item) {
      this.$emit('row-clicked', item)
    },
  },
}
</script>
